import { DeleteOutlined } from "@ant-design/icons";
import { Button, Flex, Popconfirm } from "antd";
import { FC, MouseEvent, useCallback, useMemo, useState } from "react";
import { PointDebt } from "tm-shared/types";
import { formatUAHNarrow } from "tm-shared/utils";

import { PointInfo } from "@/domains/point";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { StyledComment, StyledPaymentHeader } from "./Styled";

// --- Props ---

interface PaymentHeaderProps {
    debt: PointDebt;
    onDeletePayments: (paymentIds: string[]) => Promise<void>;
    deleteLoading: boolean;
    canDelete: boolean;
    employeeId: string;
    isManager: boolean;
}

// --- Payment header ---

export const PaymentHeader: FC<PaymentHeaderProps> = ({
    debt,
    onDeletePayments,
    deleteLoading,
    canDelete,
    employeeId,
    isManager,
}) => {
    // --- Local state ---

    const [localDeleteLoading, setLocalDeleteLoading] = useState(false);

    // --- Derived state ---

    const areAllPaymentsUnexported = useMemo(
        () =>
            debt.payments.every(
                ({ isExported, payer }) =>
                    !isExported && (isManager || payer === employeeId),
            ),
        [debt.payments, employeeId, isManager],
    );

    const hasUnexportedPayments = useMemo(
        () => debt.payments.some((p) => !p.isExported),
        [debt.payments],
    );

    // --- UI actions ---

    const getPaymentColorText = () => {
        switch (true) {
            case !debt.status:
                return "error";
            case hasUnexportedPayments:
                return "warning";
            default:
                return "primary";
        }
    };

    const handleDelete = useCallback(async () => {
        setLocalDeleteLoading(true);

        const paymentIds = debt.payments
            .filter(
                ({ isExported, payer }) =>
                    !isExported && (isManager || payer === employeeId),
            )
            .map((payment) => payment.paymentIds ?? [])
            .flat();

        await onDeletePayments(paymentIds);

        setLocalDeleteLoading(false);
    }, [debt.payments, employeeId, isManager, onDeletePayments]);

    const handleButtonClick = useCallback((evt: MouseEvent) => {
        evt.stopPropagation();
    }, []);

    // --- Render ---

    return (
        <StyledPaymentHeader
            $isCompleted={debt?.status}
            $isExported={!hasUnexportedPayments}
        >
            <Flex vertical gap={8}>
                <PointInfo point={debt?.point} />
                {debt?.comment && <StyledComment>{debt.comment}</StyledComment>}

                <Flex gap={8} justify="space-between" align="center">
                    <Flex gap={4}>
                        <SecondaryText fontSize={16}>Оплата:</SecondaryText>

                        <PrimaryText
                            fontSize={20}
                            textColor={getPaymentColorText()}
                        >
                            {formatUAHNarrow(debt?.paid ?? 0)}
                        </PrimaryText>
                    </Flex>

                    {canDelete && areAllPaymentsUnexported && (
                        <Popconfirm
                            title={"Видалити всі оплати торгової точки?"}
                            onConfirm={handleDelete}
                            okText="Так"
                            cancelText="Ні"
                            disabled={deleteLoading}
                        >
                            <Button
                                type="text"
                                loading={localDeleteLoading}
                                disabled={deleteLoading}
                                onClick={handleButtonClick}
                                icon={
                                    <PrimaryText
                                        textColor={
                                            deleteLoading ? "disabled" : "error"
                                        }
                                        fontSize={18}
                                    >
                                        <DeleteOutlined />
                                    </PrimaryText>
                                }
                            />
                        </Popconfirm>
                    )}
                </Flex>
            </Flex>
        </StyledPaymentHeader>
    );
};
